/* eslint-disable react-hooks/exhaustive-deps */
import { FieldValues, useForm } from "react-hook-form";
import { FieldTypes } from "@/api/AppApi/services/builder/interfaces/interfaces";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  useGetProfileQuery,
  useUpdateSettings,
} from "@/api/AppApi/services/auth";
import {
  adaptFieldsToPost,
  createCustomFields,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import ReceivingMethodsPopUp from "@/app/components/pages-components/checkout/checkout-helpers/receiving-settings/ReceivingMethodsPopUp";
import { useEffect } from "react";
import {
  RecievingFields,
  datatoStore,
  getSettingsFromStorage,
  recievingFieldsDefultValues,
} from "./receiving-settings-helpers/helpers";
import PopUp from "@/app/components/common-builder/pop-ups-components/pop-up-helpers/PopUp";
import SvgHomeIndicator from "@/app/components/common-builder/pop-ups-components/pop-up-helpers/SvgHomeIndicator";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import {
  receivingBranchAtom,
  receivingSettingsAtom,
} from "@/hooks/receivingHeaderAtom";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { createDelivery_methods } from "../functions";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useQueryClient } from "@tanstack/react-query";
import { storeBranchIdCookie } from "@/utils/functions/cookieOperations";

interface Props {
  onClose?: () => void;
  isOpen: boolean;
}

const ReceivingSettings: React.FC<Props> = ({ onClose, isOpen }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const mobile = useIsMobile();
  const [isSignedIn] = useAtom(isSignedInAtom);
  const [receivingBranch] = useAtom(receivingBranchAtom);
  const [receivingSetting, setReceivingSetting] = useAtom(
    receivingSettingsAtom,
  );
  const settingsFromLocalStorage = getSettingsFromStorage(receivingSetting!);
  const getProfile = useGetProfileQuery();
  const { settings } = getProfile?.data?.result ?? {};
  const hookForm = useForm();
  const getCompany = useGetCompanyQuery();
  const shippingSettings = getCompany?.data?.result?.shipping_settings;
  const delivery_methods = createDelivery_methods(shippingSettings!);
  const onSuccess = () => {
    onClose!();
    fetch("/clear-cache", {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify({ keys: [location.hostname] }),
    })
      .then(() => {
        router.refresh();
        queryClient.invalidateQueries();
        queryClient.refetchQueries({ fetchStatus: "paused" });
      })
      .catch(() => {});
  };
  const updateSettings = useUpdateSettings({
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      console.error(err);
    },
  });
  const fields = createCustomFields([
    {
      value: null,
      key: RecievingFields.deliveryTimeFieldName,
      title: "",
      is_required: true,
      type: FieldTypes.date_time,
    },
    {
      value: null,
      key: RecievingFields.pickupTimeFieldName,
      title: "",
      is_required: true,
      type: FieldTypes.date_time,
    },
  ]);
  const onSubmit = (data: FieldValues) => {
    const adaptedFields = adaptFieldsToPost(data);
    const dataToSubmit = {
      ...adaptedFields,
      ["delivery_time"]: adaptedFields?.[RecievingFields.deliveryTimeFieldName],
      delivery_address_type: "address",
    };
    delete dataToSubmit?.[RecievingFields.deliveryTimeFieldName];
    delete dataToSubmit?.["delivery_date"];
    if (isSignedIn) {
      updateSettings.mutate(dataToSubmit);
      receivingBranch?.id && storeBranchIdCookie(receivingBranch?.id!);
    } else {
      setReceivingSetting({
        ...datatoStore(dataToSubmit),
        receiving_branch: receivingBranch!,
      });
      receivingBranch?.id && storeBranchIdCookie(receivingBranch?.id!);
      onSuccess();
    }
  };
  const className = `checkout-box${mobile ? "-mobile" : ""}`;
  useEffect(() => {
    hookForm.reset({
      ...recievingFieldsDefultValues(
        isSignedIn ? settings! : settingsFromLocalStorage,
        delivery_methods,
      ),
    });
  }, [settings, receivingSetting]);
  return (
    <PopUp isOpen={isOpen} onClose={onClose}>
      {mobile && (
        <div className="indicator">
          <SvgHomeIndicator onClick={onClose} />
        </div>
      )}
      <div
        className="scrollbar"
        style={{
          height: `${mobile ? "100%" : "700px"}`,
        }}
      >
        <form onSubmit={hookForm.handleSubmit(onSubmit)} className="auth-form">
          <ReceivingMethodsPopUp
            form={hookForm}
            deliveryTimeField={fields[0]}
            pickupTimeField={fields[1]}
            className={className}
            mobile={mobile}
          />
        </form>
      </div>
    </PopUp>
  );
};
export default ReceivingSettings;
